import { Tooltip, Typography, TypographyProps } from '@mui/material';
import { FC, useLayoutEffect, useRef, useState } from 'react';

export interface ListRowClampedTextProps extends TypographyProps {
  lines?: number;
  maxWidth?: number | string;
}

export const ListRowClampedText: FC<ListRowClampedTextProps> = ({
  lines = 2,
  maxWidth = 240,
  sx,
  children,
  ...rest
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run on content/line changes to recheck overflow
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el?.parentElement) return;

    const check = () => {
      if (!ref.current) return;
      setOverflowing(ref.current.scrollHeight > ref.current.clientHeight);
    };

    const observer = new ResizeObserver(check);
    observer.observe(el.parentElement);
    check();

    return () => observer.disconnect();
  }, [children, lines]);

  const text = (
    <Typography
      ref={ref}
      {...rest}
      sx={{
        maxWidth,
        display: '-webkit-box',
        WebkitLineClamp: lines,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        wordBreak: 'break-word',
        ...sx,
      }}
    >
      {children}
    </Typography>
  );

  if (!overflowing) return text;

  return <Tooltip title={children}>{text}</Tooltip>;
};
